<template>
  <!-- Bestandsverwaltung mobil (Trello 21.08.): WMS-Chargen kompakt in 5 Spalten -->
  <div class="m-list">
    <div class="m-search">
      <i class="material-icons">search</i>
      <input v-model="keywords" :placeholder="$t('mobile.wms.searchGeneral')" type="search" @input="debouncedReload" />
      <button v-if="keywords" class="m-search__clear" @click="clearField('keywords')"><i class="material-icons">close</i></button>
    </div>
    <!-- Foto/Scanner am SKU-Feld (Trello WMS mobil, Update 28.08.): gescannte GTIN loest der Server zur SKU auf -->
    <div class="m-search-row">
      <div class="m-search">
        <i class="material-icons">qr_code_2</i>
        <input v-model="sku" :placeholder="$t('mobile.wms.searchSku')" type="search" @input="debouncedReload" />
        <button v-if="sku" class="m-search__clear" @click="clearField('sku')"><i class="material-icons">close</i></button>
      </div>
      <m-wms-photo-button kind="ean" @extracted="onPhotoResult" />
      <button type="button" class="m-wms-form__iconbtn" @click="scannerOpen = true"><i class="material-icons">qr_code_scanner</i></button>
    </div>
    <m-barcode-scanner v-if="scannerOpen" @scanned="onScanned" @close="scannerOpen = false" />
    <div class="m-search">
      <i class="material-icons">shelves</i>
      <input v-model="location" :placeholder="$t('mobile.wms.searchLocation')" type="search" @input="onLocationInput" />
      <button v-if="location" class="m-search__clear" @click="clearLocation"><i class="material-icons">close</i></button>
    </div>
    <!-- Lagerplatz-Vorschlaege wie im Wareneingang-Feld (Kundenfall 25.08.: Freitext "PP-" traf nichts) -->
    <ul v-if="locationSuggestions.length" class="m-wms-suggest">
      <li v-for="s in locationSuggestions" :key="s.id" @click="chooseLocation(s)">{{ s.fullName }}</li>
    </ul>
    <div class="m-wms-toggles">
      <label><input v-model="exactMatchSku" type="checkbox" @change="reload" /> {{ $t("mobile.wms.exactMatch") }}</label>
      <label><input v-model="sortByLocation" type="checkbox" @change="reload" /> {{ $t("mobile.wms.sortByLocation") }}</label>
      <select v-if="!assigned && warehouses.length" v-model="warehouseId" class="m-wms-toggles__select" @change="reload">
        <option :value="null">{{ $t("mobile.wms.allWarehouses") }}</option>
        <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
      </select>
    </div>

    <div v-if="loading && !items.length" class="m-hint">{{ $t("mobile.loading") }}</div>
    <div v-else-if="!items.length" class="m-hint">{{ $t("mobile.noResults") }}</div>

    <table v-if="items.length" class="m-wms-table">
      <thead>
        <tr>
          <th>{{ $t("mobile.wms.location") }} / {{ $t("mobile.wms.sku") }} / {{ $t("mobile.wms.inventory") }}</th>
          <th v-if="canSeePrices" class="text-right">{{ $t("mobile.wms.prices") }}</th>
          <th>{{ $t("mobile.wms.origin") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in items" :key="item.id">
          <!-- Lagerplatz, SKU, Bestand untereinander (Michael 24.08.): breite Spalten, nichts bricht um -->
          <td>
            <div class="m-wms-table__line">{{ item.warehouseCode }}-{{ item.storageLocation }}</div>
            <div class="m-wms-table__sku">{{ item.sku }}</div>
            <div class="m-wms-table__line">
              <strong>{{ item.inventory }}</strong>
              <span class="m-wms-table__sub" style="display: inline">· {{ $t("mobile.wms.reservedShort") }}: {{ item.reserved }}</span>
            </div>
          </td>
          <td v-if="canSeePrices" class="text-right">
            <div class="m-wms-table__line">EK: {{ item.purchasePrice | formatUnit("USD", 2, 2) }}</div>
            <div class="m-wms-table__line">BZNK: {{ ((item.additionalCosts || 0) + (item.customsItem || 0)) | formatUnit("EUR", 2, 2) }}</div>
            <div class="m-wms-table__line">BEP: {{ bep(item) | formatUnit("EUR", 2, 2) }}</div>
            <div class="m-wms-table__line">POS: {{ (bep(item) * (item.inventory + item.reserved)) | formatUnit("EUR", 2, 2) }}</div>
          </td>
          <td>
            <div class="m-wms-table__line">{{ item.date | formatDate }}</div>
            <div class="m-wms-table__sub">{{ item.source || "–" }}</div>
            <div class="m-wms-table__sub">{{ item.reference || "–" }}</div>
          </td>
        </tr>
      </tbody>
    </table>

    <button v-if="items.length < total" class="m-more" :disabled="loading" @click="loadMore">
      {{ loading ? $t("mobile.loading") : $t("mobile.loadMore", { count: total - items.length }) }}
    </button>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import WarehouseService from "@/services/warehouse.service";
import { assignedWarehouse, hasPermission } from "@/mixins/auth";
import MBarcodeScanner from "../components/MBarcodeScanner.vue";
import MWmsPhotoButton from "./MWmsPhotoButton.vue";

const PAGE_SIZE = 25;

export default {
  name: "MWarehouses",
  components: { MBarcodeScanner, MWmsPhotoButton },
  data() {
    return {
      items: [],
      total: 0,
      page: 0,
      loading: false,
      keywords: "",
      sku: "",
      scannerOpen: false,
      location: "",
      exactMatchSku: false,
      sortByLocation: true,
      warehouseId: null,
      warehouses: [],
      locations: [],
      locationSuggestions: [],
      storageLocationId: null,
      assigned: assignedWarehouse(),
    };
  },
  computed: {
    // Preisdaten wie am Desktop nur mit LimitRead-Recht (die Ausblendung ist clientseitig - Karten-Scope)
    canSeePrices() {
      return hasPermission("LimitRead", "Warehouses", "PurchasePrice");
    },
  },
  created() {
    this.debouncedReload = debounce(this.reload, 350);
    if (!this.assigned) {
      WarehouseService.getWarehouses().then((w) => (this.warehouses = w || []));
    }
    // Lagerplaetze einmal laden, lokal filtern - gleiches Muster wie MWmsLocationField
    WarehouseService.getLocationsByWarehouse({ warehouseId: 0, location: "", isPulldown: true }).then((list) => {
      this.locations = (list || []).filter((l) => l.isActive);
    });
    this.reload();
  },
  methods: {
    // Kamera-Scan / Foto: Code ins SKU-Feld, der Server loest eine GTIN zur SKU auf
    onScanned({ value }) {
      this.scannerOpen = false;
      this.sku = String(value || "").trim();
      this.reload();
    },
    onPhotoResult(r) {
      this.sku = (r && (r.sku || r.ean)) || "";
      this.reload();
    },
    clearField(name) {
      this[name] = "";
      this.reload();
    },
    onLocationInput() {
      this.storageLocationId = null;
      const q = this.location.trim().toUpperCase();
      this.locationSuggestions = q.length < 2 ? [] : this.locations.filter((l) => (l.fullName || "").toUpperCase().includes(q)).slice(0, 12);
      this.debouncedReload();
    },
    chooseLocation(s) {
      this.location = s.fullName;
      this.storageLocationId = s.id;
      this.locationSuggestions = [];
      this.reload();
    },
    clearLocation() {
      this.location = "";
      this.storageLocationId = null;
      this.locationSuggestions = [];
      this.reload();
    },
    bep(item) {
      const rate = item.exchangeRate || 0;
      const ek = rate ? (item.purchasePrice || 0) / rate : 0;
      return ek + (item.additionalCosts || 0) + (item.customsItem || 0);
    },
    async reload() {
      this.page = 0;
      this.items = [];
      await this.fetch();
    },
    async loadMore() {
      this.page += 1;
      await this.fetch();
    },
    async fetch() {
      this.loading = true;
      try {
        const res = await WarehouseService.getWarehouseInventories({
          keywords: this.keywords || undefined,
          sku: this.sku || undefined,
          exactMatchSku: this.exactMatchSku || undefined,
          location: this.storageLocationId ? undefined : this.location || undefined,
          storageLocationId: this.storageLocationId || undefined,
          warehouseId: this.assigned ? this.assigned.id : this.warehouseId || undefined,
          pageIndex: this.page,
          pageSize: PAGE_SIZE,
          orderBy: this.sortByLocation ? "location" : undefined,
          ascending: this.sortByLocation ? 1 : undefined,
        });
        this.items = this.page === 0 ? res.data || [] : this.items.concat(res.data || []);
        this.total = res.total || 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./m-wms.scss"></style>
